
import { Badge } from "./ui/badge";
import { Clock, CalendarClock, CheckSquare, ExternalLink, Upload, Mic } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Meeting } from "../types/meetings";
import { useUserTimezone } from "../hooks/useUserTimezone";
import { formatDate } from "../utils/date";
import { useIsMobile } from "../hooks/use-mobile";
import { StatusBadge } from "./shared/StatusBadge";
import { Button } from "./ui/button";
import React from "react";

interface MeetingProps {
  meeting: Meeting & {
    summary: string;
    action_items_count: number;
    status: string;
  };
}

export function MeetingCard({ meeting }: MeetingProps) {
  const navigate = useNavigate();
  const { timezone } = useUserTimezone();
  const isMobile = useIsMobile();

  const handleClick = () => {
    navigate(`/app/meeting/${meeting.id}`);
  };

  const handleViewStatus = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate('/app/status');
  };

  const getMeetingTitle = () => {
    if (!meeting.title) {
      return `Meeting from ${formatDate(meeting.created_at || new Date(), timezone)}`;
    }
    return meeting.title;
  };

  const formatDuration = (seconds: string | number | null) => {
    if (!seconds) return "0 min";
    const totalSeconds = Number(seconds);
    
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    
    if (minutes === 0) {
      return `${remainingSeconds} sec`;
    }
    
    return `${minutes}m ${remainingSeconds}s`;
  };

  const isProcessing = meeting.status === 'processing' || meeting.status === 'queued';
  const isAnalyzing = meeting.status === 'analyzing';

  const TypeIcon = meeting.type === 'upload' ? Upload : Mic;

  return (
    <div
      onClick={handleClick}
      className="group rounded-xl transition-all duration-300 cursor-pointer overflow-hidden bg-background hover:bg-muted/50 p-6"
    >
      <div className="space-y-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <TypeIcon className="h-4 w-4 text-muted-foreground" />
            <h3 className="font-medium text-md text-primary group-hover:text-primary-dark">
              {getMeetingTitle()}
            </h3>
          </div>
          {!isMobile && (isProcessing || isAnalyzing) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleViewStatus}
              className="h-8 w-8 p-0"
            >
              <ExternalLink className="h-4 w-4" />
              <span className="sr-only">View Status</span>
            </Button>
          )}
        </div>
        
        <p className="text-gray-600 line-clamp-2">
          {meeting.summary || "No summary available."}
        </p>

        <div className={`${isMobile ? 'flex flex-col space-y-2' : 'flex items-center justify-between'} text-sm text-gray-500`}>
          <div className={`${isMobile ? 'space-y-2' : 'flex items-center gap-6'}`}>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{formatDuration(meeting.duration)}</span>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <CheckSquare className="h-4 w-4" />
                <span>{meeting.action_items_count || 0} Action Item{meeting.action_items_count !== 1 ? 's' : ''}</span>
              </div>
              <StatusBadge status={meeting.status as any} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4" />
            <span>{formatDate(meeting.created_at || new Date(), timezone)}</span>
          </div>
          {isMobile && (isProcessing || isAnalyzing) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleViewStatus}
              className="h-8 w-8 p-0"
            >
              <ExternalLink className="h-4 w-4" />
              <span className="sr-only">View Status</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
